import { isNoOrgAccountSession } from "@services/auth";
import { facilityService } from "@services/facilityServices";
import { listNetworks } from "@services/networkManagement";
import { getUserPreferredFacilities } from "@services/payment";
import {
  getPreviewVertical,
  getRequestVerticalOverride,
  resolveActiveVertical,
} from "@services/verticals";
import { getFacilitiesFromLoginUser } from "@utils/facilityBootstrap";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { canAddByName, FEATURE_NAMES } from "@utils/permissions";
import {
  getCurrentUser,
  getCurrentUserId,
  isGlobalAdministrator,
  userCanUseNetworkFacility,
  userIsSalesUser,
} from "@utils/userUtils";

export interface UserFacility {
  id: number;
  facility_id?: number;
  facility_name?: string;
  npi_number?: string;
  org_name?: string;
  address_1?: string | null;
  address_2?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  telephone_number?: string | null;
  fax_number?: string | null;
  enumeration_type?: string;
  enumeration_date?: string;
  last_updated?: string;
  status?: string;
  primary_taxonomy_code?: string;
  primary_taxonomy_desc?: string;
  is_verified?: boolean;
  is_active?: boolean;
  facility_group_id?: string | null;
  parent_facility_group_id?: string | null;
  is_parent_facility?: boolean;
  is_child_facility?: boolean;
  child_facilities?: UserFacility[];
  is_affable_connected?: boolean;
  created_at?: string;
  updated_at?: string;
  full_address?: string;
  user_role?: string;
  access_level?: string;
  is_primary?: boolean;
  association_id?: number;
  facility_type_slug?: string | null;
  ccn_number?: string | null;
  medicare_ccn?: string | null;
  vertical?: string | null;
  primary_profile_slug?: string | null;
  is_profile_approved?: boolean | null;
  medicare_certification_date?: string | null;
  requires_medicare_certification?: boolean | null;
  /** Facility profile: show Title 26 tax rows in Potential Penalties (default off). */
  show_tax_compliance_calendar?: boolean | null;
  /** MCM / billing: when present, drives sidebar "Active" visibility with {@link isPaidActiveFacilityForSidebar}. */
  plan_status?: string | null;
  is_plan_active?: boolean | null;
  transaction_status?: string | null;
  /** True when access is restricted (cancelled / expired). */
  is_restricted?: boolean | null;
  plan_id?: number | null;
  /** Holding-company network row (not a facility). */
  is_network?: boolean;
  network_id?: number;
  [key: string]: unknown;
}

export interface UserFacilitiesResponse {
  success: boolean;
  message?: string;
  facilities: UserFacility[];
  count?: number;
  error?: unknown;
}

/** Rules service: GET /facility-management/facility-status-dashboard */
export interface FacilityStatusEnforcementSummary {
  years_included: number[];
  years_with_data: number[];
  total_action_count: number;
  total_amount: number;
  last_captured_at: string | null;
}

export interface FacilityStatusRollup {
  total_members: number;
  active: number;
  verified: number;
  inactive: number;
  unverified: number;
  profile_not_approved: number;
  medicare_cert_gap: number;
  child_needing_attention: number;
  orphan_children: number;
  enforcement_action_count: number;
  enforcement_total_amount: number;
}

export interface FacilityStatusMember {
  hierarchy_role: "parent" | "child" | "standalone" | "orphan_child";
  enforcement: FacilityStatusEnforcementSummary;
  facility: UserFacility;
}

export interface FacilityStatusGroup {
  facility_group_id: string;
  parent_facility_id?: number | null;
  parent_facility_name?: string | null;
  rollup: FacilityStatusRollup;
  members: FacilityStatusMember[];
}

export interface FacilityStatusDashboardResponse {
  success: boolean;
  message: string;
  country_code: string;
  enforcement_year_from: number;
  enforcement_year_to: number;
  global_rollup: FacilityStatusRollup | null;
  groups: FacilityStatusGroup[];
  standalone_members: FacilityStatusMember[];
}

const API_BASE_URL = getApiOrigin();
const USER_FACILITIES_PATH = 
  "/api/v1/facility-management/user-facilities";
const CREATE_FROM_CCN_PATH = "/api/v1/facility-management/create-from-ccn";
const FACILITY_CANCELLATION_PATH =
  "/api/v1/facility-management/facility-cancellation";
const USER_PLAN_PURCHASED_DETAILS_PATH =
  "/api/v1/facility-management/user-plan-purchased-details";

export interface FacilityCancellationPayload {
  user_associated_facility_id: number;
  facility_id: number;
  plan_id: number;
  plan_purchased_id: number;
  cancellation_reason: string;
  end_date: string;
}

export interface FacilityCancellationResponse {
  success?: boolean;
  message?: string;
}

/**
 * Request cancellation of a facility subscription / plan purchase.
 */
export async function submitFacilityCancellation(
  payload: FacilityCancellationPayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<FacilityCancellationResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(`${baseUrl}${FACILITY_CANCELLATION_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  let json: FacilityCancellationResponse | null = null;
  if (text) {
    try {
      json = JSON.parse(text) as FacilityCancellationResponse;
    } catch {
      if (!response.ok) {
        throw new Error(
          text.slice(0, 200) || `Request failed with ${response.status}`,
        );
      }
    }
  }

  if (!response.ok) {
    const errorMessage =
      json?.message || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  return json ?? { success: true, message: "" };
}

export interface FacilityResubscribePayload {
  user_associated_facility_id: number;
  facility_id: number;
  plan_purchased_id?: number;
}

export async function submitFacilityResubscribe(
  payload: FacilityResubscribePayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<FacilityCancellationResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(
    `${baseUrl}${FACILITY_CANCELLATION_PATH}/resubscribe`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const text = await response.text();
  const json = text
    ? (JSON.parse(text) as FacilityCancellationResponse)
    : null;

  if (!response.ok) {
    const errorMessage =
      json?.message || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  return json ?? { success: true, message: "" };
}

/** Fields accepted by Rules PUT user-plan-purchased-details/{record_id}. */
export interface UpdateUserPlanPurchasedDetailsPayload {
  user_id?: number;
  customer_id?: number;
  user_associated_facilities_id?: number;
  plan_id?: number;
  plan_purchased_id?: number;
  is_active?: boolean;
  is_delete?: boolean;
}

export interface UserPlanPurchasedDetailsRecord {
  id: number;
  user_id?: number;
  customer_id?: number;
  user_associated_facilities_id?: number;
  plan_id?: number;
  plan_purchased_id?: number;
  is_active?: boolean;
  is_delete?: boolean;
  [key: string]: unknown;
}

export interface UpdateUserPlanPurchasedDetailsResponse {
  success?: boolean;
  message?: string;
  result?: UserPlanPurchasedDetailsRecord;
  data?: UserPlanPurchasedDetailsRecord;
}

/**
 * Update a user_plan_purchased_details row (authenticated PUT; own rows only).
 */
export async function updateUserPlanPurchasedDetails(
  planPurchasedId: number | string,
  payload: UpdateUserPlanPurchasedDetailsPayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string },
): Promise<UpdateUserPlanPurchasedDetailsResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(
    `${baseUrl}${USER_PLAN_PURCHASED_DETAILS_PATH}/${encodeURIComponent(
      String(planPurchasedId),
    )}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const text = await response.text();
  const json = text
    ? (JSON.parse(text) as UpdateUserPlanPurchasedDetailsResponse)
    : null;

  if (!response.ok) {
    const errorMessage =
      json?.message || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  return json ?? { success: true, message: "" };
}

export type { FacilityCancelImpact } from "../types/cancelImpact";

/**
 * Facility impact metrics for cancel-subscription review (AB#10918).
 */
export async function getFacilityCancelImpact(
  facilityId: number,
  options?: {
    accessEndDate?: string;
    baseUrlOverride?: string;
    accessTokenOverride?: string;
  },
): Promise<import("../types/cancelImpact").FacilityCancelImpact> {
  const rawBase =
    options?.baseUrlOverride !== undefined
      ? options.baseUrlOverride
      : API_BASE_URL || "";
  const baseUrl = String(rawBase).replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const params = new URLSearchParams();
  if (options?.accessEndDate) {
    params.set("access_end_date", options.accessEndDate);
  }
  const qs = params.toString();
  const url = `${baseUrl}/api/v1/facilities/${facilityId}/cancel-impact${
    qs ? `?${qs}` : ""
  }`;

  const response = await fetchFn(url, { method: "GET" });
  const text = await response.text();
  let json: import("../types/cancelImpact").FacilityCancelImpact | null = null;
  if (text) {
    try {
      json = JSON.parse(text) as import("../types/cancelImpact").FacilityCancelImpact;
    } catch {
      json = null;
    }
  }

  if (!response.ok) {
    throw new Error(`Request failed with ${response.status}`);
  }

  if (!json) {
    throw new Error("Empty cancel-impact response");
  }
  return json;
}

const FACILITY_STATUS_DASHBOARD_PATH =
  "/api/v1/facility-management/facility-status-dashboard";

const _userFacilitiesInFlight = new Map<string, Promise<UserFacilitiesResponse>>();

function _userFacilitiesSingleFlightKey(
  userId: number,
  countryCode: string,
  vertical: string,
): string {
  return `${userId}\0${countryCode}\0${vertical}`;
}

export interface ParentFacilityGroupResponse {
  success: boolean;
  message?: string;
  facility_id?: number;
  facility_group_id?: string | null;
  parent_facility_group_id?: string | null;
  error?: unknown;
}

export interface CreateFacilityFromCcnPayload {
  ccn_number?: string;
  facility_name: string;
  city: string;
  state: string;
  country: string;
  facility_type: string;
  facility_type_id?: number;
  username: string;
  pricing_tier_id: number;
  phone_number?: string;
  skip_ccn_validation?: boolean;
  address_1?: string;
  address_2?: string;
  postal_code?: string;
  vertical?: string;
  primary_profile_slug?: string;
  profile_targeting_json?: Record<string, unknown>;
}

export interface CreateFacilityFromCcnResponse {
  success?: boolean;
  message?: string;
  facility_id?: number;
  association_id?: number;
  error?: unknown;
}

/** Mint or attach UserAssociatedFacility for add-by-type / slug-only AFH. */
export async function createFacilityFromCcn(
  payload: CreateFacilityFromCcnPayload,
): Promise<CreateFacilityFromCcnResponse> {
  const baseUrl = (API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  const response = await authenticatedFetch(`${baseUrl}${CREATE_FROM_CCN_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const text = await response.text();
  const json = text ? (JSON.parse(text) as CreateFacilityFromCcnResponse) : null;
  if (!response.ok || json?.success === false) {
    const detail =
      json?.message ||
      (typeof json?.error === "object" && json?.error
        ? JSON.stringify(json.error)
        : "") ||
      text ||
      `Request failed with ${response.status}`;
    throw new Error(detail);
  }
  return json as CreateFacilityFromCcnResponse;
}

export async function getUserFacilities(
  userId: number,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    /** Vertical slug filter; defaults to healthcare when omitted. */
    vertical?: string;
  }
): Promise<UserFacilitiesResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const countryCode = getSelectedCountryCode();
  const vertical = (options?.vertical || "healthcare").trim().toLowerCase() || "healthcare";
  const singleFlightKey = _userFacilitiesSingleFlightKey(userId, countryCode, vertical);
  const useSingleFlight = !options?.accessTokenOverride && !options?.baseUrlOverride;
  if (useSingleFlight) {
    const existing = _userFacilitiesInFlight.get(singleFlightKey);
    if (existing) {
      return existing;
    }
  }

  const execute = async (): Promise<UserFacilitiesResponse> => {
    const url = new URL(`${baseUrl}${USER_FACILITIES_PATH}`);
    url.searchParams.set("user_id", String(userId));
    url.searchParams.set("country_code", countryCode);
    url.searchParams.set("vertical", vertical);

    const fetchFn = options?.accessTokenOverride
      ? (u: string, o: RequestInit) =>
          fetch(u, {
            ...o,
            headers: {
              ...Object.fromEntries(new Headers(o.headers).entries()),
              Authorization: `Bearer ${options.accessTokenOverride}`,
            },
          })
      : authenticatedFetch;

    const response = await fetchFn(url.toString(), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const text = await response.text();
    const json = text ? (JSON.parse(text) as UserFacilitiesResponse) : null;

    if (!response.ok) {
      const errorMessage = json?.message || `Request failed with ${response.status}`;
      throw new Error(errorMessage);
    }

    return json as UserFacilitiesResponse;
  };

  if (!useSingleFlight) {
    return execute();
  }

  const promise = execute().finally(() => {
    _userFacilitiesInFlight.delete(singleFlightKey);
  });
  _userFacilitiesInFlight.set(singleFlightKey, promise);
  return promise;
}

export async function getFacilityStatusDashboard(
  userId: number,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    enforcement_year_from?: number;
    enforcement_year_to?: number;
  }
): Promise<FacilityStatusDashboardResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const url = new URL(`${baseUrl}${FACILITY_STATUS_DASHBOARD_PATH}`);
  url.searchParams.set("user_id", String(userId));
  url.searchParams.set("country_code", getSelectedCountryCode());
  if (options?.enforcement_year_from != null) {
    url.searchParams.set(
      "enforcement_year_from",
      String(options.enforcement_year_from),
    );
  }
  if (options?.enforcement_year_to != null) {
    url.searchParams.set(
      "enforcement_year_to",
      String(options.enforcement_year_to),
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const text = await response.text();
  const json = text
    ? (JSON.parse(text) as FacilityStatusDashboardResponse)
    : null;

  if (!response.ok) {
    const errorMessage =
      json?.message || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  return json as FacilityStatusDashboardResponse;
}

export async function declareParentFacilityGroupId(
  facilityId: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<ParentFacilityGroupResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }
  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;
  const response = await fetchFn(
    `${baseUrl}/api/v1/facility-management/facility/${facilityId}/parent-group-id`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const text = await response.text();
  const json = text ? (JSON.parse(text) as ParentFacilityGroupResponse) : null;
  if (!response.ok) {
    throw new Error(json?.message || `Request failed with ${response.status}`);
  }
  return json as ParentFacilityGroupResponse;
}

export async function acceptParentFacilityGroupId(
  facilityId: number,
  parentFacilityGroupId: string,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<ParentFacilityGroupResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }
  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;
  const response = await fetchFn(
    `${baseUrl}/api/v1/facility-management/facility/${facilityId}/parent-group-id/accept`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parent_facility_group_id: parentFacilityGroupId }),
    }
  );
  const text = await response.text();
  const json = text ? (JSON.parse(text) as ParentFacilityGroupResponse) : null;
  if (!response.ok) {
    throw new Error(json?.message || `Request failed with ${response.status}`);
  }
  return json as ParentFacilityGroupResponse;
}

export interface MedicareCertPrefillData {
  original_certification_date?: string | null;
  next_certification_date?: string | null;
  /** Saved facilities.medicare_certification_date (YYYY-MM-DD), if any. */
  profile_certification_date?: string | null;
  /** CMS POS lookup date (YYYY-MM-DD), if the server ran a lookup. */
  cms_pos_certification_date?: string | null;
  /** True when profile and CMS POS dates both exist and disagree (SSOT-60). */
  certification_date_conflict?: boolean;
  accrediting_body?: { slug: string; name: string }[] | null;
  /** Authoritative flag from the server-side MEDICARE_CERTIFICATION_REQUIRED_SLUGS list.
   *  Use this (not the presence of a CMS date) to decide whether to show the Medicare
   *  recertification date field. */
  requires_medicare_certification?: boolean;
  /**
   * CMS / cache diagnostics for server debugging. Global admins see this in the raw
   * prefill JSON; other users get plain-language text derived from it in the Hub UI.
   */
  lookup_diagnostics?: Record<string, unknown> | null;
  medicare_revalidation_step_years?: number;
  is_dmepos_supplier?: boolean;
  lab_anchor_active?: boolean;
}

export type MedicareCertPrefillError = {
  ok: false;
  status: number;
  url: string;
  message: string;
  error_code?: string;
  detail?: unknown;
};

export type MedicareCertPrefillSuccess = { ok: true; data: MedicareCertPrefillData };

export type MedicareCertPrefillResult = MedicareCertPrefillSuccess | MedicareCertPrefillError;

/** Response data from GET .../ccn-from-cms-pos */
export interface CcnFromCmsPosData {
  ccn: string;
  matched_fac_name?: string | null;
  prvdr_ctgry_cd?: string | null;
}

export type CcnFromCmsPosError = {
  ok: false;
  status: number;
  url: string;
  message: string;
  error_code?: string;
};

export type CcnFromCmsPosSuccess = { ok: true; data: CcnFromCmsPosData };

export type CcnFromCmsPosResult = CcnFromCmsPosSuccess | CcnFromCmsPosError;

/** Resolve CCN (PRVDR_NUM) from CMS POS by facility name/state/city; non-throwing. */
export async function fetchCcnFromCmsPos(
  facilityId: number,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    forceRefresh?: boolean;
  },
): Promise<CcnFromCmsPosResult> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const qs = options?.forceRefresh === true ? "?force_refresh=true" : "";
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/ccn-from-cms-pos${qs}`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, { method: "GET", headers: { "Content-Type": "application/json" } });
  let json: Record<string, unknown> | null = null;
  try {
    const text = await response.text();
    json = text ? (JSON.parse(text) as Record<string, unknown>) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const errObj = json?.error;
    const code =
      errObj && typeof errObj === "object" && "code" in errObj
        ? String((errObj as { code?: unknown }).code ?? "")
        : "";
    const message =
      (typeof json?.message === "string" && json.message) ||
      `CCN lookup failed: ${response.status}`;
    return {
      ok: false,
      status: response.status,
      url,
      message,
      error_code: code || undefined,
    };
  }

  if (json && json.success === false) {
    const errObj = json.error;
    const code =
      errObj && typeof errObj === "object" && errObj !== null && "code" in errObj
        ? String((errObj as { code?: unknown }).code ?? "")
        : "";
    const message =
      (typeof json.message === "string" && json.message) ||
      "CMS did not return a CCN for this facility.";
    return {
      ok: false,
      status: response.status,
      url,
      message,
      error_code: code || undefined,
    };
  }

  const data = (json?.data ?? json) as CcnFromCmsPosData;
  if (!data?.ccn) {
    return {
      ok: false,
      status: response.status,
      url,
      message: "CCN was missing from the server response.",
    };
  }
  return { ok: true, data };
}

export interface UpdateFacilityProfilePayload {
  accrediting_body?: { slug: string; name: string }[] | null;
  medicare_certification_date?: string | null;
}

/** Non-throwing: use for manual “Lookup CMS” actions that need structured error UI. */
export async function fetchMedicareCertPrefill(
  facilityId: number,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    /** When true, server always runs CMS lookup and skips Redis read-through cache (Hub button). */
    forceRefresh?: boolean;
  },
): Promise<MedicareCertPrefillResult> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const qs =
    options?.forceRefresh === true
      ? "?force_refresh=true"
      : "";
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/medicare-cert-prefill${qs}`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, { method: "GET", headers: { "Content-Type": "application/json" } });
  let json: Record<string, unknown> | null = null;
  try {
    const text = await response.text();
    json = text ? (JSON.parse(text) as Record<string, unknown>) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    const errObj = json?.error;
    const code =
      errObj && typeof errObj === "object" && "code" in errObj
        ? String((errObj as { code?: unknown }).code ?? "")
        : "";
    const message =
      (typeof json?.message === "string" && json.message) ||
      (typeof json?.detail === "string" && json.detail) ||
      `Medicare cert prefill fetch failed: ${response.status}`;
    return {
      ok: false,
      status: response.status,
      url,
      message,
      error_code: code || undefined,
      detail: json?.detail ?? json,
    };
  }
  const data = (json?.data ?? json) as MedicareCertPrefillData;
  return { ok: true, data };
}

export async function getMedicareCertPrefill(
  facilityId: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string },
): Promise<MedicareCertPrefillData> {
  const result = await fetchMedicareCertPrefill(facilityId, options);
  if (!result.ok) {
    throw new Error(result.message);
  }
  return result.data;
}

export async function updateFacilityProfile(
  facilityId: number,
  payload: UpdateFacilityProfilePayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<void> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/profile`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`updateFacilityProfile failed: ${response.status} ${text}`);
  }
}

// ---------------------------------------------------------------------------
// Dynamic Facility Profile Schema types
// ---------------------------------------------------------------------------

export interface ProfileFieldOption {
  key: string;
  label: string;
}

export interface ProfileFieldResponse {
  field_key: string;
  label: string;
  field_type: string;
  hub_renderer_key?: string | null;
  validation_json?: Record<string, unknown> | null;
  placeholder?: string | null;
  help_text?: string | null;
  note_text?: string | null;
  is_required: boolean;
  visible: boolean;
  options?: ProfileFieldOption[] | null;
  /** Value envelope: {"value": <actual>} or null */
  value?: { value: unknown } | null;
  /** 'saved' | 'facilities_column' | 'pos_lookup' | 'npi_lookup' | 'blank' */
  value_source: string;
  is_user_confirmed: boolean;
  /**
   * Optional CMS-derived compliance hint.
   * 'compliant_tbd' — provider found in the CMS Revalidation Due Date List but
   *                   revalidation is > ~6 months out (no specific date yet, compliant).
   */
  cms_status?: string | null;
  /** UUID string when provided by API (dev / layout). */
  field_definition_id?: string | null;
  region_key?: string | null;
  layout_row?: number;
  layout_column_span?: number;
  layout_width_pct?: number | null;
  layout_order?: number;
}

export interface ProfileRegionResponse {
  id: string;
  region_key: string;
  label: string;
  subtitle?: string | null;
  display_order: number;
  style?: Record<string, unknown> | null;
}

export interface ProfileSectionResponse {
  section_key: string;
  label: string;
  description?: string | null;
  color?: string | null;
  display_order: number;
  regions?: ProfileRegionResponse[];
  fields: ProfileFieldResponse[];
}

export interface FacilityProfileUiConfig {
  profile_mode?: string;
  show_tab_strip?: boolean;
}

export interface FacilityProfileJurisdictionContext {
  state?: string | null;
  country_code?: string | null;
  state_operating_license_model?: string | null;
  pharmacy_permit_model?: string | null;
  radiation_license_model?: string | null;
  food_permit_model?: string | null;
  clia_model?: string | null;
  con_model?: string | null;
  state_lab_license_model?: string | null;
  licensed_bed_model?: string | null;
  state_license_renewal_cycle_years?: number | null;
  state_license_renewal_submit_lead_days?: number | null;
  state_license_renewal_anchor_kind?: string | null;
  state_license_renewal_rule_notes?: string | null;
  profile_notes?: string | null;
  source_title?: string | null;
  source_url?: string | null;
  source_authority?: string | null;
  source_confidence?: string | null;
  reviewed?: boolean;
}

export interface FacilityProfileSchemaResponse {
  facility: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
    country_code: string;
    facility_type_slug?: string | null;
    facility_type_display_name?: string | null;
    profile_group_type?: string | null;
    profile_group_label?: string | null;
  };
  sections: ProfileSectionResponse[];
  ui?: FacilityProfileUiConfig | null;
  jurisdiction_context?: FacilityProfileJurisdictionContext | null;
}

export interface ProfileFieldValue {
  field_key: string;
  value: unknown;
}

export interface SaveProfileValuesRequest {
  values: ProfileFieldValue[];
  approve_profile: boolean;
  skip_compliance_cache_invalidation?: boolean;
}

export interface SaveProfileValuesResponse {
  saved_count: number;
  approved: boolean;
}

const PROFILE_SCHEMA_CACHE_TTL_MS = 120_000;
const _profileSchemaCache = new Map<
  number,
  { at: number; data: FacilityProfileSchemaResponse }
>();
const _profileSchemaInFlight = new Map<number, Promise<FacilityProfileSchemaResponse>>();
/** Bumped on invalidate so in-flight GETs cannot re-seed a stale cache after save. */
let _profileSchemaCacheEpoch = 0;

export function invalidateFacilityProfileSchemaCache(facilityId?: number): void {
  _profileSchemaCacheEpoch += 1;
  if (facilityId != null) {
    _profileSchemaCache.delete(facilityId);
    _profileSchemaInFlight.delete(facilityId);
    return;
  }
  _profileSchemaCache.clear();
  _profileSchemaInFlight.clear();
}

export function clearFacilityProfileSchemaCachesForTests(): void {
  _profileSchemaCache.clear();
  _profileSchemaInFlight.clear();
  _profileSchemaCacheEpoch = 0;
}

export async function getFacilityProfileSchema(
  facilityId: number,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    bypassCache?: boolean;
    /** Skip CMS/POS prefill when dashboard BFF already warmed assembled schema cache. */
    skipExternalPrefill?: boolean;
  }
): Promise<FacilityProfileSchemaResponse> {
  const useClientCache = !options?.baseUrlOverride && !options?.accessTokenOverride;
  const now = Date.now();
  if (useClientCache && !options?.bypassCache) {
    const cached = _profileSchemaCache.get(facilityId);
    if (cached && now - cached.at < PROFILE_SCHEMA_CACHE_TTL_MS) {
      return cached.data;
    }
    const inflight = _profileSchemaInFlight.get(facilityId);
    if (inflight) {
      return inflight;
    }
  }

  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const url = new URL(
    `${baseUrl}/api/v1/facility-management/facility/${facilityId}/profile-schema`,
  );
  if (options?.skipExternalPrefill) {
    url.searchParams.set("skip_external_prefill", "true");
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const execute = async (): Promise<FacilityProfileSchemaResponse> => {
    const epochAtStart = _profileSchemaCacheEpoch;
    const response = await fetchFn(url.toString(), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      throw new Error(`getFacilityProfileSchema failed: ${response.status}`);
    }
    const data = (await response.json()) as FacilityProfileSchemaResponse;
    if (useClientCache && epochAtStart === _profileSchemaCacheEpoch) {
      _profileSchemaCache.set(facilityId, { at: Date.now(), data });
    }
    return data;
  };

  if (!useClientCache) {
    return execute();
  }

  const promise = execute().finally(() => {
    _profileSchemaInFlight.delete(facilityId);
  });
  _profileSchemaInFlight.set(facilityId, promise);
  return promise;
}

const SAVE_PROFILE_TIMEOUT_MS = 15_000;

export class SaveFacilityProfileError extends Error {
  readonly userMessage: string;

  constructor(userMessage: string, options?: { cause?: unknown }) {
    super(userMessage, options);
    this.name = "SaveFacilityProfileError";
    this.userMessage = userMessage;
  }
}

export function mapSaveFacilityProfileError(err: unknown): string {
  if (err instanceof SaveFacilityProfileError) {
    return err.userMessage;
  }
  if (err instanceof DOMException && (err.name === "AbortError" || err.name === "TimeoutError")) {
    return "Save timed out. Check your connection and try again.";
  }
  if (err instanceof Error && err.name === "TimeoutError") {
    return "Save timed out. Check your connection and try again.";
  }
  return "Save failed. Please try again.";
}

const SAVE_PROFILE_CLIENT_ERROR_FALLBACK =
  "Save failed. Please check your entries and try again.";

function extractFastApiErrorDetail(parsed: unknown): string | null {
  if (typeof parsed === "string" && parsed.trim()) {
    return parsed.trim();
  }
  if (Array.isArray(parsed)) {
    const messages = parsed
      .map((item) => extractFastApiErrorDetail(item))
      .filter((message): message is string => Boolean(message));
    return messages.length ? messages.join(" ") : null;
  }
  if (!parsed || typeof parsed !== "object") {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  if (record.detail !== undefined) {
    const fromDetail = extractFastApiErrorDetail(record.detail);
    if (fromDetail) {
      return fromDetail;
    }
  }
  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim();
  }
  if (typeof record.msg === "string" && record.msg.trim()) {
    return record.msg.trim();
  }
  return null;
}

/** Pull FastAPI `detail` / `message` from a save-error body; never dump raw JSON. */
export function formatSaveFacilityProfileClientError(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return SAVE_PROFILE_CLIENT_ERROR_FALLBACK;
  }
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const detail = extractFastApiErrorDetail(JSON.parse(trimmed) as unknown);
      if (detail) {
        return detail;
      }
    } catch {
      /* hide unreadable JSON from the alert */
    }
    return SAVE_PROFILE_CLIENT_ERROR_FALLBACK;
  }
  return trimmed;
}

function isSaveProfileTimeoutError(err: unknown): boolean {
  return (
    (err instanceof DOMException &&
      (err.name === "AbortError" || err.name === "TimeoutError")) ||
    (err instanceof Error && err.name === "TimeoutError")
  );
}

export async function saveFacilityProfileValues(
  facilityId: number,
  payload: SaveProfileValuesRequest,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<SaveProfileValuesResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/profile-values`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  let response: Response;
  try {
    response = await fetchFn(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(SAVE_PROFILE_TIMEOUT_MS),
    });
  } catch (err) {
    if (isSaveProfileTimeoutError(err)) {
      throw new SaveFacilityProfileError(
        "Save timed out. Check your connection and try again.",
        { cause: err },
      );
    }
    throw err;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    if (response.status >= 500) {
      throw new SaveFacilityProfileError("Save failed on the server. Please try again.", {
        cause: text,
      });
    }
    if (response.status >= 400) {
      throw new SaveFacilityProfileError(formatSaveFacilityProfileClientError(text), {
        cause: text,
      });
    }
    throw new SaveFacilityProfileError("Save failed. Please try again.", { cause: text });
  }
  const saved = (await response.json()) as SaveProfileValuesResponse;
  // Save Profile then switches to Dashboard; returning to Profile must not
  // replay the 120s client schema cache (AB#10679).
  invalidateFacilityProfileSchemaCache(facilityId);
  return saved;
}

export interface DeleteProfileValuesResponse {
  deleted_count: number;
}

export async function deleteProfileValues(
  facilityId: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<DeleteProfileValuesResponse> {
  const baseUrl = (options?.baseUrlOverride || API_BASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/profile-values`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`deleteProfileValues failed: ${response.status} ${text}`);
  }
  const deleted = (await response.json()) as DeleteProfileValuesResponse;
  invalidateFacilityProfileSchemaCache(facilityId);
  return deleted;
}

export interface AccreditingBodyData {
  accrediting_body?: { slug: string; name: string }[] | null;
  requires_medicare_certification?: boolean;
}

/**
 * Normalises the raw `accrediting_body` value from the API.
 * The backend may return either an array of slug strings (["cms"]) or an array
 * of objects ([{ slug: "cms", name: "..." }]).  This converts both to the
 * canonical object form used throughout the frontend.
 */
function normaliseAccreditingBody(
  raw: unknown,
): { slug: string; name: string }[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw
    .map((entry) => {
      if (typeof entry === "string") return { slug: entry, name: entry };
      if (entry && typeof entry === "object" && "slug" in entry)
        return entry as { slug: string; name: string };
      return null;
    })
    .filter((e): e is { slug: string; name: string } => e !== null);
}

const ACCREDITING_BODY_CACHE_TTL_MS = 90_000;
const _accreditingBodyCache = new Map<
  number,
  { at: number; data: AccreditingBodyData }
>();
const _accreditingBodyInflight = new Map<number, Promise<AccreditingBodyData>>();

export async function getAccreditingBody(
  facilityId: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<AccreditingBodyData> {
  const now = Date.now();
  const cached = _accreditingBodyCache.get(facilityId);
  if (
    cached &&
    !options?.accessTokenOverride &&
    now - cached.at < ACCREDITING_BODY_CACHE_TTL_MS
  ) {
    return cached.data;
  }

  const inflight = _accreditingBodyInflight.get(facilityId);
  if (inflight) {
    return inflight;
  }

  const baseUrl = (
    options?.baseUrlOverride ||
    getApiOrigin() ||
    API_BASE_URL ||
    ""
  ).replace(/\/$/, "");
  const url = `${baseUrl}/api/v1/facility-management/facility/${facilityId}/accrediting-body`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const run = (async (): Promise<AccreditingBodyData> => {
    if (!baseUrl) {
      return { accrediting_body: null };
    }
    try {
      const response = await fetchFn(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Accrediting body fetch failed: ${response.status}`);
      }
      const json = await response.json();
      const raw = (json?.data ?? json) as AccreditingBodyData & {
        accrediting_body?: unknown;
      };
      const out: AccreditingBodyData = {
        ...raw,
        accrediting_body: normaliseAccreditingBody(raw.accrediting_body),
      };
      if (!options?.accessTokenOverride) {
        _accreditingBodyCache.set(facilityId, { at: Date.now(), data: out });
      }
      return out;
    } catch (err) {
      // Network down, CORS, wrong API origin, invalid JSON — avoid uncaught rejections
      // and CRA overlay; do not cache failures (next open can retry).
      console.warn(
        "[getAccreditingBody] request failed; is the Rules API running and REACT_APP_API_BASEURL set?",
        err,
      );
      return { accrediting_body: null };
    }
  })();

  _accreditingBodyInflight.set(facilityId, run);
  run.finally(() => {
    _accreditingBodyInflight.delete(facilityId);
  });
  return run;
}

const HAD_FACILITY_ACCESS_KEY = "unicomply.hadFacilityAccess";
const FACILITY_ACCESS_ALLOWED_KEY = "unicomply.facilityAccessAllowedAt";
const FACILITY_ACCESS_ALLOWED_TTL_MS = 30_000;

function getHadFacilityAccessKey(): string {
  return `${HAD_FACILITY_ACCESS_KEY}:${getCurrentUserId()}`;
}

function getFacilityAccessAllowedKey(): string {
  return `${FACILITY_ACCESS_ALLOWED_KEY}:${getCurrentUserId()}`;
}

export function markHadFacilityAccess(): void {
  try {
    sessionStorage.setItem(getHadFacilityAccessKey(), "1");
  } catch {
    /* ignore */
  }
}

export function hasRecentFacilityAccessAllowed(): boolean {
  try {
    const raw = sessionStorage.getItem(getFacilityAccessAllowedKey());
    const timestamp = raw ? Number(raw) : 0;
    return Number.isFinite(timestamp) && Date.now() - timestamp < FACILITY_ACCESS_ALLOWED_TTL_MS;
  } catch {
    return false;
  }
}

function markFacilityAccessAllowed(): void {
  try {
    sessionStorage.setItem(getFacilityAccessAllowedKey(), String(Date.now()));
  } catch {
    /* ignore */
  }
}

function clearFacilityAccessAllowed(): void {
  try {
    sessionStorage.removeItem(getFacilityAccessAllowedKey());
  } catch {
    /* ignore */
  }
}

function isExemptFromFacilityAccessGate(): boolean {
  // SSO/email signup no-org onboarding has a Rules user id but no facilities yet;
  // FacilityAccessGuard must not treat that as revoked access.
  return (
    isNoOrgAccountSession() ||
    isGlobalAdministrator() ||
    userIsSalesUser() ||
    userCanUseNetworkFacility()
  );
}

function shouldBlockFacilityAccess(input: {
  myCount: number;
  preferredCount: number;
  networksCount: number;
  associationCount: number;
}): boolean {
  if (isExemptFromFacilityAccessGate()) return false;
  if (input.myCount > 0 || input.preferredCount > 0 || input.networksCount > 0) {
    return false;
  }
  if (input.associationCount > 0) return true;
  try {
    if (sessionStorage.getItem(getHadFacilityAccessKey()) === "1") return true;
  } catch {
    /* ignore */
  }
  if ((getFacilitiesFromLoginUser(getCurrentUser()) ?? []).length > 0) {
    return true;
  }
  if (canAddByName(FEATURE_NAMES.ADD_FACILITY)) return false;
  return true;
}

/** True when the user has no active facility access and should see /access-removed. */
export async function resolveFacilityAccessBlocked(): Promise<boolean> {
  // no_org / sales / global-admin / network: Dashboard Case 1 handles empty setup.
  if (isExemptFromFacilityAccessGate()) return false;

  const userId = parseInt(getCurrentUserId(), 10);
  if (!Number.isFinite(userId) || userId <= 0) return false;

  try {
    const vertical =
      (getRequestVerticalOverride() ||
        getPreviewVertical() ||
        (await resolveActiveVertical())).trim().toLowerCase() ||
      "healthcare";
    const [myRows, preferredRes, userFacRes, networks] = await Promise.all([
      facilityService.getMyFacilities({ vertical, bypassCache: true }),
      getUserPreferredFacilities(1, 20, userId),
      getUserFacilities(userId, { vertical }),
      userCanUseNetworkFacility() ? listNetworks() : Promise.resolve([]),
    ]);

    if (myRows.length > 0) markHadFacilityAccess();

    const preferred = (preferredRes.result?.data ?? []).filter((f) => !f.is_delete);
    const blocked = shouldBlockFacilityAccess({
      myCount: myRows.length,
      preferredCount: preferred.length,
      networksCount: networks.length,
      associationCount: userFacRes.facilities?.length ?? 0,
    });
    if (blocked) {
      clearFacilityAccessAllowed();
    } else {
      markFacilityAccessAllowed();
    }
    return blocked;
  } catch {
    return false;
  }
}

const facilityManagementService = {
  getUserFacilities,
  hasRecentFacilityAccessAllowed,
  resolveFacilityAccessBlocked,
  markHadFacilityAccess,
  declareParentFacilityGroupId,
  acceptParentFacilityGroupId,
  fetchMedicareCertPrefill,
  fetchCcnFromCmsPos,
  getMedicareCertPrefill,
  updateFacilityProfile,
  getAccreditingBody,
  getFacilityProfileSchema,
  saveFacilityProfileValues,
  deleteProfileValues,
  submitFacilityCancellation,
  submitFacilityResubscribe,
  updateUserPlanPurchasedDetails,
  getFacilityCancelImpact,
};

export default facilityManagementService;


